import React, { useState, useRef, useEffect } from "react";
import api from "../../../chat/api";

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const Pill = ({ label, isActive, category, onToggle }) => (
  <button
    type="button" 
    onClick={(e) => {
      e.preventDefault(); 
      onToggle(category, label);
    }}
    className={`px-3 py-1 rounded-full border text-[13px] font-medium transition-colors ${
      isActive
        ? "bg-[#8B55DD] border-[#8B55DD] text-white" 
        : "bg-white border-[#E5E7EB] text-[#00000080] hover:border-[#D1D5DB]"
    }`}
  >
    {label}
  </button>
);

const FilterDropdown = ({ 
  initialFilters = { fileTypes: [], statuses: [], tags: [] }, 
  onApply, 
  onClear,
  tagsRefreshTrigger = 0,
  projectId // 1. Added projectId prop
}) => {
  // 2. Make the storage key unique per project
  const STORAGE_KEY = `projectFiles_saved_filters_${projectId || 'default'}`;

  const loadInitialState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (error) {
      console.error("Failed to parse saved filters:", error);
    }
    return initialFilters;
  };

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(loadInitialState);
  const [appliedFilters, setAppliedFilters] = useState(loadInitialState); 
  const dropdownRef = useRef(null);

  const [fetchedTags, setFetchedTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [hasFetchedTags, setHasFetchedTags] = useState(false);

  const hasSelections = selected.fileTypes?.length > 0 || selected.statuses?.length > 0 || selected.tags?.length > 0;
  const isDirty = JSON.stringify(selected) !== JSON.stringify(appliedFilters);

  // 3. Handle project navigation (if the user clicks a different project without unmounting the component)
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSelected(parsed);
        setAppliedFilters(parsed);
        if (onApply) onApply(parsed);
      } catch(e) {}
    } else {
      // If no saved filters for this specific project, reset to blank
      const clearedState = { fileTypes: [], statuses: [], tags: [] };
      setSelected(clearedState);
      setAppliedFilters(clearedState);
      if (onApply) onApply(clearedState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, STORAGE_KEY]); 

  // Sync if parent component manually overrides filters
  useEffect(() => {
    if (JSON.stringify(initialFilters) !== JSON.stringify(appliedFilters)) {
      setSelected(initialFilters);
      setAppliedFilters(initialFilters);
    }
  }, [JSON.stringify(initialFilters)]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setHasFetchedTags(false);
  }, [tagsRefreshTrigger]);

  useEffect(() => {
    if (isOpen && !hasFetchedTags && !isLoadingTags) {
      const fetchTags = async () => {
        setIsLoadingTags(true);
        try {
          const res = await api.get("/api/tagging/tags/");
          const tagNames = res.data.map((tag) => tag.name);
          setFetchedTags(tagNames);
          setHasFetchedTags(true);
        } catch (error) {
          console.error("Failed to fetch tags:", error);
        } finally {
          setIsLoadingTags(false);
        }
      };
      fetchTags();
    }
  }, [isOpen, hasFetchedTags, isLoadingTags]);

  const handleToggle = (category, value) => {
    setSelected((prev) => {
      const list = prev[category] || []; 
      return {
        ...prev,
        [category]: list.includes(value) ? list.filter((item) => item !== value) : [...list, value],
      };
    });
  };

  const handleApplyClick = () => {
    setAppliedFilters(selected);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
    if (onApply) onApply(selected);
    setIsOpen(false);
  };

  const handleClearClick = () => {
    const clearedState = { fileTypes: [], statuses: [], tags: [] };
    setSelected(clearedState);
    
    const hasAppliedFilters = appliedFilters.fileTypes?.length > 0 || 
                              appliedFilters.statuses?.length > 0 || 
                              appliedFilters.tags?.length > 0;
    
    if (hasAppliedFilters) {
      setAppliedFilters(clearedState);
      localStorage.removeItem(STORAGE_KEY);
      
      if (onClear) {
        onClear();
      } else if (onApply) {
        onApply(clearedState);
      }
    }
    setIsOpen(false); 
  };

  return (
    <div className="relative inline-block font-outfit" ref={dropdownRef}>
      <div className="relative inline-block">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-4 py-1.5 border rounded text-[14px] font-medium transition-colors ${
            isOpen || hasSelections
              ? "border-[#8B55DD] text-[#8B55DD] bg-[#F8F3FF]"
              : "border-[#00000036] text-[#52525B] hover:bg-gray-50"
          }`}
        >
          <FilterIcon /> Filter
        </button>
        {hasSelections && (
          <span className="absolute top-0 right-0 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white transform translate-x-1/2 -translate-y-1/2"></span>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-[340px] bg-white rounded-xl shadow-[0px_4px_20px_rgba(0,0,0,0.08)] border border-[#E5E7EB] z-[999] p-5">
          <div className="flex justify-between items-center mb-5">
            <h3 className="text-[16px] font-semibold text-[#1A1A1A]">Filters</h3>
            <button type="button" onClick={() => setIsOpen(false)} className="text-[#A1A1AA] hover:text-[#52525B] transition-colors">
              <CloseIcon />
            </button>
          </div>

          <div className="flex flex-col gap-5">
            <div>
              <h4 className="text-[13px] text-[#00000080] mb-2 font-medium">File Type</h4>
              <div className="flex flex-wrap gap-2">
                {["PDF", "DOCX", "XLSX"].map((type) => (
                  <Pill key={type} label={type} isActive={selected.fileTypes?.includes(type)} category="fileTypes" onToggle={handleToggle} />
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-[13px] text-[#00000080] mb-2 font-medium">Status</h4>
              <div className="flex flex-wrap gap-2">
                {["Ready For Review", "Ingested", "Rejected"].map((status) => (
                  <Pill key={status} label={status} isActive={selected.statuses?.includes(status)} category="statuses" onToggle={handleToggle} />
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-[13px] text-[#00000080] mb-2 font-medium">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {isLoadingTags ? (
                  <span className="text-[13px] text-[#A1A1AA]">Loading tags...</span>
                ) : fetchedTags.length > 0 ? (
                  fetchedTags.map((tag) => (
                    <Pill key={tag} label={tag} isActive={selected.tags?.includes(tag)} category="tags" onToggle={handleToggle} />
                  ))
                ) : (
                  <span className="text-[13px] text-[#A1A1AA]">No tags available.</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end items-center gap-3 mt-8">
            <button
              type="button"
              onClick={handleClearClick}
              disabled={!hasSelections}
              className={`px-4 py-1.5 border rounded text-[14px] font-medium transition-colors ${
                hasSelections
                  ? "border-[#E5E7EB] text-[#52525B] hover:bg-gray-50"
                  : "border-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Clear Filters
            </button>
            
            <button
              type="button"
              onClick={handleApplyClick}
              disabled={!isDirty}
              className={`px-6 py-1.5 rounded text-[14px] font-medium transition-colors ${
                isDirty
                  ? "bg-[#8B55DD] text-white hover:bg-[#7a48c4]"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;